import React, { useState, useEffect } from 'react';
import { Card } from "../ui/card.jsx";
import { Label } from "../ui/label.jsx";
import { Button } from "../ui/button.jsx";
import { Badge } from "../ui/badge.jsx";
import { ScrollArea } from "../ui/scroll-area.jsx";
import { Input } from "../ui/input.jsx";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover.jsx";
import { Calendar } from "../ui/calendar.jsx";
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { motion, AnimatePresence } from "framer-motion";
import { 
  FilterX, 
  CircleDashed, 
  CircleDot, 
  AlertTriangle, 
  Clock, 
  CheckCircle2, 
  XCircle,
  Building,
  Filter,
  Calendar as CalendarIcon,
  Search,
  ChevronDown,
  User,
  Tag,
  Binary,
  SlidersHorizontal,
  RefreshCw
} from 'lucide-react';

export default function TicketFilters({ filters, setFilters, companies, clients = [], users = [], isInternalUser }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [dateRange, setDateRange] = useState({
    from: null,
    to: null
  });
  const [searchTerm, setSearchTerm] = useState('');

  // Persistência dos filtros
  useEffect(() => {
    const saved = localStorage.getItem('ticketFilters');
    if (saved) {
      const parsedFilters = JSON.parse(saved);
      setFilters(parsedFilters);
      // Sincronizar searchTerm com o valor do filtro
      if (parsedFilters.searchTerm) {
        setSearchTerm(parsedFilters.searchTerm);
      }
    }
  }, []);
  
  useEffect(() => {
    localStorage.setItem('ticketFilters', JSON.stringify(filters));
  }, [filters]);

  // Sincronizar searchTerm quando filters.searchTerm mudar
  useEffect(() => {
    if (filters.searchTerm !== searchTerm) {
      setSearchTerm(filters.searchTerm || '');
    }
  }, [filters.searchTerm]);

  // Função para alternar seleção múltipla
  function toggleFilter(key, value) {
    setFilters(prev => {
      let arr = Array.isArray(prev[key]) ? prev[key] : prev[key] === 'all' ? [] : [prev[key]];
      if (arr.includes(value)) {
        arr = arr.filter(v => v !== value);
      } else {
        arr.push(value);
      }
      return { ...prev, [key]: arr.length === 0 ? 'all' : arr };
    });
  }

  const clearFilters = () => {
    setFilters(prev => ({
      status: 'all',
      priority: 'all',
      type: 'all',
      company: 'all',
      client: 'all',
      assignee: 'all',
      dateRange: null,
      searchTerm: ''
    }));
    setDateRange({ from: null, to: null });
    setSearchTerm('');
  };

  const handleSearch = (value) => {
    setSearchTerm(value);
    setFilters(prev => ({ ...prev, searchTerm: value }));
  };

  const handleDateRangeChange = (range) => {
    setDateRange(range);
    setFilters(prev => ({ ...prev, dateRange: range }));
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (filters.status !== 'all') count++;
    if (filters.priority !== 'all') count++;
    if (filters.type !== 'all') count++;
    if (filters.company !== 'all') count++;
    if (filters.client !== 'all') count++;
    if (filters.assignee !== 'all') count++;
    if (dateRange.from || dateRange.to) count++;
    if (searchTerm) count++;
    return count;
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col md:flex-row gap-4">
        {/* Search Bar */}
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            placeholder="Buscar por título, descrição ou ID..."
            value={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
            className="pl-10 bg-background"
          />
        </div>

        {/* Quick Filter Buttons */}
        <div className="flex gap-2 flex-wrap md:flex-nowrap">
          <Button
            variant={Array.isArray(filters.status) ? filters.status.includes('open') : filters.status === 'open' ? 'default' : 'outline'}
            size="sm"
            onClick={() => toggleFilter('status', 'open')}
            className={`flex items-center gap-2 border ${Array.isArray(filters.status) ? filters.status.includes('open') : filters.status === 'open' ? 'bg-red-100 text-red-800 border-red-300 ring-2 ring-red-400' : ''}`}
          >
            <CircleDot className="h-4 w-4 text-red-500" />
            <span>Em Aberto</span>
          </Button>

          <Button
            variant={Array.isArray(filters.status) ? filters.status.includes('in_progress') : filters.status === 'in_progress' ? 'default' : 'outline'}
            size="sm"
            onClick={() => toggleFilter('status', 'in_progress')}
            className={`flex items-center gap-2 border ${Array.isArray(filters.status) ? filters.status.includes('in_progress') : filters.status === 'in_progress' ? 'bg-blue-100 text-blue-800 border-blue-300 ring-2 ring-blue-400' : ''}`}
          >
            <Clock className="h-4 w-4 text-blue-500" />
            <span>Em Andamento</span>
          </Button>

          <Button
            variant={Array.isArray(filters.status) ? filters.status.includes('analysis') : filters.status === 'analysis' ? 'default' : 'outline'}
            size="sm"
            onClick={() => toggleFilter('status', 'analysis')}
            className={`flex items-center gap-2 border ${Array.isArray(filters.status) ? filters.status.includes('analysis') : filters.status === 'analysis' ? 'bg-purple-100 text-purple-800 border-purple-300 ring-2 ring-purple-400' : ''}`}
          >
            <AlertTriangle className="h-4 w-4 text-purple-500" />
            <span>Em Análise</span>
          </Button>

          <Button
            variant={Array.isArray(filters.status) ? filters.status.includes('resolved') : filters.status === 'resolved' ? 'default' : 'outline'}
            size="sm"
            onClick={() => toggleFilter('status', 'resolved')}
            className={`flex items-center gap-2 border ${Array.isArray(filters.status) ? filters.status.includes('resolved') : filters.status === 'resolved' ? 'bg-green-100 text-green-800 border-green-300 ring-2 ring-green-400' : ''}`}
          >
            <CheckCircle2 className="h-4 w-4 text-green-500" />
            <span>Concluídos</span>
          </Button>

          {/* Filtros de Responsável - apenas para usuários internos */}
          {isInternalUser && (
            <>
              <Button
                variant={Array.isArray(filters.assignee) ? filters.assignee.includes('unassigned') : filters.assignee === 'unassigned' ? 'default' : 'outline'}
                size="sm"
                onClick={() => toggleFilter('assignee', 'unassigned')}
                className={`flex items-center gap-2 border ${Array.isArray(filters.assignee) ? filters.assignee.includes('unassigned') : filters.assignee === 'unassigned' ? 'bg-gray-100 text-gray-800 border-gray-300 ring-2 ring-gray-400' : ''}`}
              >
                <User className="h-4 w-4 text-gray-500" />
                <span>Não Atribuídos</span>
              </Button>

              <Button
                variant={Array.isArray(filters.assignee) ? filters.assignee.includes('me') : filters.assignee === 'me' ? 'default' : 'outline'}
                size="sm"
                onClick={() => toggleFilter('assignee', 'me')}
                className={`flex items-center gap-2 border ${Array.isArray(filters.assignee) ? filters.assignee.includes('me') : filters.assignee === 'me' ? 'bg-indigo-100 text-indigo-800 border-indigo-300 ring-2 ring-indigo-400' : ''}`}
              >
                <User className="h-4 w-4 text-indigo-500" />
                <span>Meus Chamados</span>
              </Button>
            </>
          )}

          {/* Advanced Filters Button */}
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className={`flex items-center gap-2 ${getActiveFiltersCount() > 0 ? 'border-primary text-primary' : ''}`}
              >
                <SlidersHorizontal className="h-4 w-4" />
                <span>Filtros</span>
                {getActiveFiltersCount() > 0 && (
                  <Badge variant="secondary" className="ml-1">
                    {getActiveFiltersCount()}
                  </Badge>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-full max-w-md p-4 sm:w-96 sm:max-w-lg" align="end" sideOffset={8} style={{ minWidth: '280px' }}>
              <div className="space-y-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0">
                  <h4 className="font-medium">Filtros Avançados</h4>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={clearFilters}
                    className="h-8 px-2 text-muted-foreground hover:text-foreground"
                  >
                    <FilterX className="w-4 h-4 mr-1" />
                    Limpar
                  </Button>
                </div>

                {/* Status Section */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Status</Label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {[
                      { value: 'open', label: 'Aberto', icon: CircleDot, color: 'text-red-500' },
                      { value: 'in_progress', label: 'Em Andamento', icon: Clock, color: 'text-blue-500' },
                      { value: 'resolved', label: 'Resolvido', icon: CheckCircle2, color: 'text-green-500' },
                      { value: 'closed', label: 'Fechado', icon: XCircle, color: 'text-gray-500' }
                    ].map(status => (
                      <Button
                        key={status.value}
                        variant={Array.isArray(filters.status) ? filters.status.includes(status.value) : filters.status === status.value ? "default" : "outline"}
                        size="sm"
                        onClick={() => toggleFilter('status', status.value)}
                        className="justify-start"
                      >
                        <status.icon className={`h-4 w-4 mr-2 ${status.color}`} />
                        {status.label}
                      </Button>
                    ))}
                  </div>
                </div>

                {/* Priority Section */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Prioridade</Label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {[
                      { value: 'low', label: 'Baixa', color: 'bg-blue-500' },
                      { value: 'medium', label: 'Média', color: 'bg-yellow-500' },
                      { value: 'high', label: 'Alta', color: 'bg-orange-500' },
                      { value: 'critical', label: 'Crítica', color: 'bg-red-500' }
                    ].map(priority => (
                      <Button
                        key={priority.value}
                        variant={Array.isArray(filters.priority) ? filters.priority.includes(priority.value) : filters.priority === priority.value ? "default" : "outline"}
                        size="sm"
                        onClick={() => toggleFilter('priority', priority.value)}
                        className="justify-start"
                      >
                        <div className={`h-3 w-3 rounded-full ${priority.color} mr-2`} />
                        {priority.label}
                      </Button>
                    ))}
                  </div>
                </div>

                {/* Type Section */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Tipo</Label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {[
                      { value: 'support', label: 'Suporte', icon: Tag, color: 'bg-blue-100 text-blue-800 border-blue-300' },
                      { value: 'development', label: 'Desenvolvimento', icon: Binary, color: 'bg-green-100 text-green-800 border-green-300' },
                      { value: 'project', label: 'Projeto', icon: Building, color: 'bg-yellow-100 text-yellow-800 border-yellow-300' }
                    ].map(type => {
                      const isActive = Array.isArray(filters.type) ? filters.type.includes(type.value) : filters.type === type.value;
                      return (
                      <Button
                        key={type.value}
                          variant={isActive ? "default" : "outline"}
                        size="sm"
                        onClick={() => toggleFilter('type', type.value)}
                          className={`justify-start border ${isActive ? type.color + ' ring-2 ring-primary' : ''}`}
                      >
                        <type.icon className="h-4 w-4 mr-2" />
                        {type.label}
                      </Button>
                      );
                    })}
                  </div>
                </div>

                {/* Date Range Section */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Período</Label>
                  <div className="grid grid-cols-1 gap-2">
                    <div className="flex flex-col space-y-2">
                      <Calendar
                        mode="range"
                        selected={dateRange}
                        onSelect={handleDateRangeChange}
                        locale={ptBR}
                        className="rounded-md border"
                      />
                    </div>
                  </div>
                </div>

                {/* Assignee Section (if internal user) */}
                {isInternalUser && users.length > 0 && (
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Responsável</Label>
                    <ScrollArea className="h-32 max-h-40 rounded-md border bg-background">
                      <div className="p-2 space-y-1">
                        <Button
                          variant={Array.isArray(filters.assignee) ? filters.assignee.includes('unassigned') : filters.assignee === 'unassigned' ? "default" : "ghost"}
                          size="sm"
                          onClick={() => toggleFilter('assignee', 'unassigned')}
                          className="w-full justify-start"
                        >
                          <User className="h-4 w-4 mr-2" />
                          Não atribuídos
                        </Button>
                        <Button
                          variant={Array.isArray(filters.assignee) ? filters.assignee.includes('me') : filters.assignee === 'me' ? "default" : "ghost"}
                          size="sm"
                          onClick={() => toggleFilter('assignee', 'me')}
                          className="w-full justify-start"
                        >
                          <User className="h-4 w-4 mr-2" />
                          Meus chamados
                        </Button>
                        {users.map((user) => (
                          <Button
                            key={user.id}
                            variant={Array.isArray(filters.assignee) ? filters.assignee.includes(user.id) : filters.assignee === user.id ? "default" : "ghost"}
                            size="sm"
                            onClick={() => toggleFilter('assignee', user.id)}
                            className="w-full justify-start"
                          >
                            <User className="h-4 w-4 mr-2" />
                            {user.name}
                          </Button>
                        ))}
                      </div>
                    </ScrollArea>
                  </div>
                )}

                {/* Clients Section (if internal user) */}
                {isInternalUser && clients.length > 0 && (
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Cliente</Label>
                    <ScrollArea className="h-32 max-h-40 rounded-md border bg-background">
                      <div className="p-2 space-y-1">
                        {clients.map((client) => (
                          <Button
                            key={client.id}
                            variant={Array.isArray(filters.client) ? filters.client.includes(client.id) : filters.client === client.id ? "default" : "ghost"}
                            size="sm"
                            onClick={() => toggleFilter('client', client.id)}
                            className="w-full justify-start"
                          >
                            <User className="h-4 w-4 mr-2" />
                            {client.corporate_name}
                          </Button>
                        ))}
                      </div>
                    </ScrollArea>
                  </div>
                )}

                {/* Companies Section (if internal user) */}
                {isInternalUser && companies.length > 0 && (
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Empresa</Label>
                    <ScrollArea className="h-32 max-h-40 rounded-md border bg-background">
                      <div className="p-2 space-y-1">
                        {companies.map((company) => (
                          <Button
                            key={company.id}
                            variant={Array.isArray(filters.company) ? filters.company.includes(company.id) : filters.company === company.id ? "default" : "ghost"}
                            size="sm"
                            onClick={() => toggleFilter('company', company.id)}
                            className="w-full justify-start"
                          >
                            <Building className="h-4 w-4 mr-2" />
                            {company.name || company.company_name}
                          </Button>
                        ))}
                      </div>
                    </ScrollArea>
                  </div>
                )}
              </div>
            </PopoverContent>
          </Popover>

          {/* Reset Filters */}
          {getActiveFiltersCount() > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearFilters}
              className="flex items-center gap-2"
            >
              <RefreshCw className="h-4 w-4" />
              <span>Resetar</span>
            </Button>
          )}
        </div>
      </div>

      {/* Active Filters Display */}
      <AnimatePresence>
        {getActiveFiltersCount() > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="flex flex-wrap gap-2"
          >
            {filters.status !== 'all' && (
              <Badge variant="secondary" className="cursor-pointer hover:bg-muted" onClick={() => setFilters(prev => ({ ...prev, status: 'all' }))}>
                Status: {Array.isArray(filters.status) ? filters.status.map(s => s === 'open' ? 'Em Aberto' : s === 'in_progress' ? 'Em Andamento' : s === 'resolved' ? 'Resolvido' : 'Fechado').join(', ') : filters.status}
                <XCircle className="w-3 h-3 ml-1" />
              </Badge>
            )}
            {filters.priority !== 'all' && (
              <Badge variant="secondary" className="cursor-pointer hover:bg-muted" onClick={() => setFilters(prev => ({ ...prev, priority: 'all' }))}>
                Prioridade: {Array.isArray(filters.priority) ? filters.priority.map(p => p === 'low' ? 'Baixa' : p === 'medium' ? 'Média' : p === 'high' ? 'Alta' : 'Crítica').join(', ') : filters.priority}
                <XCircle className="w-3 h-3 ml-1" />
              </Badge>
            )}
            {filters.type !== 'all' && (
              <Badge variant="secondary" className="cursor-pointer hover:bg-muted" onClick={() => setFilters(prev => ({ ...prev, type: 'all' }))}>
                Tipo: {Array.isArray(filters.type) ? filters.type.map(t => t === 'support' ? 'Suporte' : t === 'development' ? 'Desenvolvimento' : 'Projeto').join(', ') : filters.type}
                <XCircle className="w-3 h-3 ml-1" />
              </Badge>
            )}
            {filters.assignee !== 'all' && (
              <Badge variant="secondary" className="cursor-pointer hover:bg-muted" onClick={() => setFilters(prev => ({ ...prev, assignee: 'all' }))}>
                Responsável: {Array.isArray(filters.assignee) ? 
                  filters.assignee.map(a => 
                    a === 'unassigned' ? 'Não atribuídos' : 
                    a === 'me' ? 'Meus chamados' :
                    users.find(u => u.id === a)?.name || 'Usuário'
                  ).join(', ') :
                  filters.assignee === 'unassigned' ? 'Não atribuídos' :
                  filters.assignee === 'me' ? 'Meus chamados' :
                  users.find(u => u.id === filters.assignee)?.name || 'Usuário'
                }
                <XCircle className="w-3 h-3 ml-1" />
              </Badge>
            )}
            {filters.client !== 'all' && (
              <Badge variant="secondary" className="cursor-pointer hover:bg-muted" onClick={() => setFilters(prev => ({ ...prev, client: 'all' }))}>
                Cliente: {Array.isArray(filters.client) ? filters.client.map(c => clients.find(client => client.id === c)?.name || clients.find(client => client.id === c)?.corporate_name || 'Cliente').join(', ') : clients.find(client => client.id === filters.client)?.name || clients.find(client => client.id === filters.client)?.corporate_name || 'Cliente'}
                <XCircle className="w-3 h-3 ml-1" />
              </Badge>
            )}
            {filters.company !== 'all' && (
              <Badge variant="secondary" className="cursor-pointer hover:bg-muted" onClick={() => setFilters(prev => ({ ...prev, company: 'all' }))}>
                Empresa: {Array.isArray(filters.company) ? filters.company.map(c => companies.find(company => company.id === c)?.name || companies.find(company => company.id === c)?.company_name || 'Empresa').join(', ') : companies.find(company => company.id === filters.company)?.name || companies.find(company => company.id === filters.company)?.company_name || 'Empresa'}
                <XCircle className="w-3 h-3 ml-1" />
              </Badge>
            )}
            {(dateRange.from || dateRange.to) && (
              <Badge variant="secondary" className="cursor-pointer hover:bg-muted" onClick={() => handleDateRangeChange({ from: null, to: null })}>
                Período: {dateRange.from ? format(dateRange.from, 'dd/MM/yyyy') : ''}
                {dateRange.to ? ` - ${format(dateRange.to, 'dd/MM/yyyy')}` : ''}
                <XCircle className="w-3 h-3 ml-1" />
              </Badge>
            )}
            {searchTerm && (
              <Badge variant="secondary" className="cursor-pointer hover:bg-muted" onClick={() => handleSearch('')}>
                Busca: {searchTerm}
                <XCircle className="w-3 h-3 ml-1" />
              </Badge>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}